<template>
  <div class="etg-select" :class="classes">
    <n-config-provider :theme-overrides="themeOverrides">
      <n-select
        v-model:value="value"
        :options="props.options"
        :show-checkmark="false"
        :disabled="props.disabled"
        v-bind="omit($attrs, 'onChange', 'class')"
      >
        <template #arrow>
          <svg-icon class="transition-color" :class="props.disabled && 'text-gray-60'" name="arrow-down" />
        </template>
      </n-select>
    </n-config-provider>
  </div>
</template>

<script setup lang="ts">
import type { GlobalThemeOverrides } from 'naive-ui'
import { NConfigProvider, NSelect } from 'naive-ui'
import { merge, omit } from 'lodash-es'

import { colors } from '@/theme.config'

import type { EtgSelectProps, SelectType } from '@/types/component'

const props = withDefaults(defineProps<EtgSelectProps>(), {
  withBorder: true,
  options: () => [],
  type: 'default',
  size: 'medium',
  block: false,
  disabled: false,
})

const value = defineModel<EtgSelectProps['options'][number]['value']>()

const classes = computed(() => {
  const sizeClasses = {
    mini: 'select-xs',
    small: 'select-sm',
    medium: 'select-md',
  }

  return [sizeClasses[props.size], props.block && 'w-full', `select-${props.type}`]
})

const themeOverrides = computed<GlobalThemeOverrides>(() => {
  const noBorderThemes = {
    border: 'none',
    borderHover: 'none',
    borderActive: 'none',
    borderFocus: 'none',
    boxShadowActive: 'none',
    boxShadowFocus: 'none',
  }
  const selectionStyles: Record<SelectType, Record<string, string>> = {
    default: {
      textColor: colors.black[50],
      arrowColor: colors.black[50],
    },
    text: {
      textColor: colors.black[50],
      arrowColor: colors.black[50],
      ...noBorderThemes,
    },
    'primary-text': {
      textColor: colors.red[50],
      arrowColor: colors.red[50],
      ...noBorderThemes,
    },
  }

  const baseThemes: GlobalThemeOverrides = {
    common: {
      heightMedium: '40px',
    },
    Select: {
      peers: {
        InternalSelection: {
          paddingSingle: '8px 48px 8px 16px',
          arrowSize: '20px',
          ...(!props.withBorder && noBorderThemes),
          ...selectionStyles[props.type],
        },
        InternalSelectMenu: {
          paddingMedium: '8px 16px',
          optionTextColorPressed: colors.black[50],
          optionTextColorActive: colors.red[50],
          optionColorActive: colors.white,
          optionPaddingMedium: '12px 20px',
          optionFontSizeMedium: '16px',
        },
      },
    },
  }

  return merge(baseThemes, props.nUiThemeOverrides)
})
</script>

<style lang="scss" scoped>
.etg-select :deep(.n-select) {
  .n-base-selection {
    @apply min-h-auto leading-initial;

    .n-base-selection-label {
      @apply h-auto;
    }
  }
}

.etg-select.select-xs :deep(.n-select) {
  .n-base-selection-input {
    @apply pl-3 py-0.5 pr-7.5 text-sm;
  }

  .n-base-loading.n-base-suffix {
    @apply right-3;
  }

  .n-base-loading,
  .n-base-clear,
  .n-base-icon,
  .n-base-icon svg {
    @apply size-4;
  }
}

.etg-select.select-sm :deep(.n-select) {
  .n-base-selection-input {
    @apply pl-3 py-1 pr-9 text-base;
  }

  .n-base-loading.n-base-suffix {
    @apply right-3;
  }

  .n-base-loading,
  .n-base-clear,
  .n-base-icon,
  .n-base-icon svg {
    @apply size-5;
  }
}

.etg-select.select-md :deep(.n-select) {
  .n-base-selection-input {
    @apply pl-4 py-2 pr-12 text-lg;
  }

  .n-base-loading.n-base-suffix {
    @apply right-4;
  }

  .n-base-loading,
  .n-base-clear,
  .n-base-icon,
  .n-base-icon svg {
    @apply size-6;
  }
}

.etg-select:where(.select-text, .select-primary-text) {
  :deep(.n-base-selection--active) {
    .n-base-selection-input {
      @apply bg-gray-40 rounded;
    }
  }
}
</style>
<style lang="scss">
.n-select-menu.n-base-select-menu .n-base-select-option::before {
  @apply left-0 right-0;
}
</style>
